import { ContactActivityRequest, ContactActivityType, ContactId, ContactResponse } from '@business/entities/contact';
import { CommonService } from '@business/services';
import { Button } from '@core/components/Button';
import { FormCheckbox, FormContainer, FormDateInput, FormSelect } from '@core/components/form';
import { FormHTML } from '@core/components/form/FormHTML';
import { formControl, FormDataType, formGroup, useFormConfig, useFormState } from '@core/utils/form';
import { toMoment } from '@core/utils/time';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { Observable } from 'rxjs';
import { NotesView } from './TabNotes';

function defineNoteForm(contactId: ContactId) {
  return formGroup({
    note: formControl<string>({ required: true }),
    forDate: formControl<number>({ required: true, initialValue: toMoment().unix() }),
    contact: formControl<ContactId>({ required: true, initialValue: contactId }),
    isFirst: formControl<boolean>({ label: 'First Time Interview' }),
  });
}

type NoteFormData = FormDataType<typeof defineNoteForm>;

export interface NewNoteProps {
  contactId: ContactId;
  contacts: ContactResponse[];
  onAdd(activity: ContactActivityRequest): Observable<any>;
  type: NotesView;
  onClose(): void;
}

export const NewNote = memo((props: NewNoteProps) => {
  const { contactId, contacts, onClose, type } = props;
  const form = useMemo(() => defineNoteForm(contactId), [contactId]);

  const contactItems = useMemo(() => CommonService.selectItems(contacts, 'name', 'id'), [contacts]);

  const isInterview = useMemo(() => {
    return type === NotesView.interview || type === NotesView.interviewInPerson;
  }, [type]);

  const onSubmit = useCallback(
    (data: NoteFormData) => {
      let note = '';
      let noteType = ContactActivityType.note;

      if (type === NotesView.note) {
        note = `Note<br />${data.note}`;
        noteType = ContactActivityType.note;
      } else if (type === NotesView.interview) {
        if (data.isFirst) {
          note = `Call - 1st Time Interview<br />${data.note}`;
        } else {
          note = `Call - Interview<br />${data.note}`;
        }
        noteType = ContactActivityType.interview;
      } else if (type === NotesView.interviewInPerson) {
        note = `Interview - In Person<br />${data.note}`;
        noteType = ContactActivityType.interviewInPerson;
      } else if (type === NotesView.metting) {
        note = `Call - Scheduled Meeting<br />${data.note}`;
        noteType = ContactActivityType.meeting;
      } else if (type === NotesView.other) {
        note = `Call - Other<br />${data.note}`;
        noteType = ContactActivityType.other;
      }
      return props.onAdd({ notesResult: note, forDate: data.forDate, contact: data.contact, firstInterview: data.isFirst, activityType: noteType });
    },
    [props, type],
  );

  useEffect(() => form.patchValue({ forDate: toMoment().unix(), isFirst: false, note: '' }, { emit: false }), [form, type]);

  useFormConfig(form, { onSubmit });

  const { submitting } = useFormState(form, { submitting: true });
  return (
    <div className="mt-4">
      <FormContainer onSubmit={form.submit}>
        <FormHTML control={form.controls.note} full autoFocus={true} />
        {contacts.length > 1 && <FormSelect control={form.controls.contact} items={contactItems} />}
        {isInterview && <FormCheckbox control={form.controls.isFirst} />}

        <FormDateInput control={form.controls.forDate} />
        <div className="submit">
          <Button type="submit" intent="primary" loading={submitting}>
            Submit
          </Button>
          <a onClick={onClose} className="btn btn-primary-link">
            Cancel
          </a>
        </div>
      </FormContainer>
    </div>
  );
});
